import PermissionButton, { currentCan } from '../../permissions/PermissionButton';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Card,
  Table,
  Input,
  Button,
  Space,
  Tabs,
  Popconfirm,
  message,
  Drawer,
  DatePicker,
  Form,
  Tooltip,
  Checkbox,
  Select
} from 'antd';
import {
  SearchOutlined,
  DeleteOutlined,
  LoadingOutlined,
  PlusOutlined,
  SwapOutlined,
  FilterOutlined,
  ClearOutlined,
  PrinterOutlined,
  CloudUploadOutlined,
  DownloadOutlined,
  MoreOutlined,
  CloseOutlined,
  RollbackOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { ReloadOutlined } from '@ant-design/icons';
import { useFetchAllDevis, useTransferDevis, useUnvalidateDevis, useDeleteDevis } from '../../hooks/devisHooks';
import DevisForm from './DevisForm';
import dayjs from 'dayjs';
import PrintDialog from './PrintDialog';
import { openVentileDevis } from './VentilePrintUtility';
import { printDevis, previewDevis, initializePrintTemplates, printDevisEmplacement, previewDevisEmplacement } from './DevisPrintUtility';
import { useFetchDevisById, useFetchItemsInDevis } from '../../hooks/devisHooks';
import { useFetchComById, useFetchAllComs } from '../../hooks/comHooks';
import axios from 'axios';
import ExportProgress from '../shared/ExportProgress';
import downloadService from '../../services/downloadService';
import DevisDropFileSelector from './DevisDropFileSelector';

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

// Constants for localStorage
const ACTIVE_TAB_KEY = 'devis_active_tab';
const SELECTED_COMMERCIALS_KEY = 'devis_selected_commercials';
const LIST_STATE_KEY = 'devis_list_state';

const readListState = () => {
  try {
    return JSON.parse(localStorage.getItem(LIST_STATE_KEY)) || {};
  } catch (_) {
    return {};
  }
};

const restoreDate = (value) => {
  if (typeof value !== 'string') return null;
  const date = dayjs(value);
  return date.isValid() ? date : null;
};

const restoreSearch = (value) => typeof value === 'string' ? value : '';
const restorePageNumber = (value, fallback) => Number.isInteger(value) && value > 0 ? value : fallback;

const DevisList = ({ onSelectDevis, userPermissions = { isManager: false, isDevisManager: false }, captureClickEvent = false }) => {
  const [savedListState] = useState(readListState);
  const [clientSearch, setClientSearch] = useState(() => restoreSearch(savedListState.clientSearch));
  const [compteSearch, setCompteSearch] = useState(() => restoreSearch(savedListState.compteSearch));
  const [refSearch, setRefSearch] = useState(() => restoreSearch(savedListState.refSearch));
  const [dateRange, setDateRange] = useState(() => [
    restoreDate(savedListState.dateRange?.[0]),
    restoreDate(savedListState.dateRange?.[1]),
  ]);
  const [pagination, setPagination] = useState(() => ({
    current: restorePageNumber(savedListState.pagination?.current, 1),
    pageSize: restorePageNumber(savedListState.pagination?.pageSize, 20),
  }));
  
  // Initialize activeTab from localStorage or default to 'notValidated'
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const savedTab = localStorage.getItem(ACTIVE_TAB_KEY);
      return ['notValidated', 'validated', 'transferred'].includes(savedTab) ? savedTab : 'notValidated';
    } catch (_) {
      return 'notValidated';
    }
  });

  // Restore sorting, falling back to the active tab's default for older saved state.
  const [sorter, setSorter] = useState(() => {
    const savedSorter = savedListState.sorter;
    if (['REF_DEV', 'DATE_DEV', 'DATEVALID', 'MNT_TTC'].includes(savedSorter?.field)
      && ['ascend', 'descend', null].includes(savedSorter.order)) {
      return { field: savedSorter.field, order: savedSorter.order };
    }
    return {
      field: 'REF_DEV',
      order: activeTab === 'transferred' ? 'descend' : 'ascend'
    };
  });
  
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isTransferring, setIsTransferring] = useState(false);

  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const [selectedDevisForPrint, setSelectedDevisForPrint] = useState(null);
  const { devis: selectedDevisData } = useFetchDevisById(selectedDevisForPrint);
  const { items: selectedDevisItems } = useFetchItemsInDevis(selectedDevisForPrint);
  const [commercialId, setCommercialId] = useState(null);
  const { Com: commercialData } = useFetchComById(commercialId);

  const [isBulkPrinting, setIsBulkPrinting] = useState(false);
  const [currentPrintingIndex, setCurrentPrintingIndex] = useState(0);
  const [shouldBreakPrinting, setShouldBreakPrinting] = useState(false);
  const [selectedDevisForBulkPrint, setSelectedDevisForBulkPrint] = useState([]);

  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(null);

  useEffect(() => {
    if (isExporting || !['completed', 'failed'].includes(exportProgress?.stage)) return;
    const timer = setTimeout(() => setExportProgress(null), 3000);
    return () => clearTimeout(timer);
  }, [isExporting, exportProgress?.stage]);

  const [showDateFilters, setShowDateFilters] = useState(() => savedListState.showDateFilters === true);
  const [startDate, setStartDate] = useState(() => restoreDate(savedListState.startDate));
  const [endDate, setEndDate] = useState(() => restoreDate(savedListState.endDate));
  const [isCheckingImport, setIsCheckingImport] = useState(false);
  const [isFileSelectorVisible, setIsFileSelectorVisible] = useState(false);
  const [selectedCommercials, setSelectedCommercials] = useState(() => {
    try {
      const raw = localStorage.getItem(SELECTED_COMMERCIALS_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      // Support both legacy numeric arrays and [{value,label}] arrays
      if (Array.isArray(parsed)) {
        if (parsed.length > 0 && typeof parsed[0] === 'object' && parsed[0] !== null && 'value' in parsed[0]) {
          // Already in labelInValue format
          return parsed.map((it) => ({ value: parseInt(it.value, 10), label: it.label }));
        }
        // Legacy numeric array -> convert to objects with placeholder label
        return parsed
          .map((v) => parseInt(v, 10))
          .filter((n) => !Number.isNaN(n))
          .map((n) => ({ value: n, label: String(n) }));
      }
      return [];
    } catch (_) {
      return [];
    }
  });
  const { Coms: allCommercials, loading: comLoading } = useFetchAllComs();

  // Save activeTab to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(ACTIVE_TAB_KEY, activeTab);
    } catch (_) {
      // Keep the list usable when browser storage is unavailable.
    }
  }, [activeTab]);

  useEffect(() => {
    try {
      localStorage.setItem(LIST_STATE_KEY, JSON.stringify({
        clientSearch,
        compteSearch,
        refSearch,
        dateRange: dateRange.map(date => date ? date.format('YYYY-MM-DD') : null),
        pagination,
        sorter,
        showDateFilters,
        startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
        endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      }));
    } catch (_) {
      // Keep filtering functional when browser storage is unavailable.
    }
  }, [clientSearch, compteSearch, refSearch, dateRange, pagination, sorter, showDateFilters, startDate, endDate]);

  // Persist selected commercials to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(SELECTED_COMMERCIALS_KEY, JSON.stringify(selectedCommercials || []));
    } catch (_) {
      // ignore storage errors
    }
  }, [selectedCommercials]);

  // Enrich labels from fetched commercials list
  useEffect(() => {
    if (!allCommercials || !Array.isArray(allCommercials)) return;
    setSelectedCommercials((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const updated = prev.map((sc) => {
        const code = typeof sc === 'object' ? sc.value : sc;
        const found = allCommercials.find((c) => c.code_com === code);
        const desiredLabel = found ? `${found.nom} (${found.code_com})` : (typeof sc === 'object' ? sc.label : String(code));
        if (typeof sc === 'object') {
          if (sc.label === desiredLabel) return sc;
          return { value: code, label: desiredLabel };
        }
        return { value: code, label: desiredLabel };
      });
      return updated;
    });
  }, [allCommercials]);

  useEffect(() => {
    if (selectedDevisData?.CODE_COM) {
      setCommercialId(selectedDevisData.CODE_COM);
    }
  }, [selectedDevisData?.CODE_COM]);

  useEffect(() => {
    initializePrintTemplates().catch(console.error);
  }, []);

  const params = useMemo(() => ({
    page: pagination.current,
    pageSize: pagination.pageSize,
    clientSearch,
    compteSearch,
    refSearch,
    dateFrom: dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
    dateTo: dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
    sortField: sorter.field,
    sortOrder: sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : undefined,
    status: activeTab,
    commercials: selectedCommercials.length ? selectedCommercials.map((it) => (typeof it === 'object' ? it.value : it)) : undefined,
  }), [pagination.current, pagination.pageSize, clientSearch, compteSearch, refSearch, dateRange, sorter.field, sorter.order, activeTab, selectedCommercials]);

  const { devis, total, loading, error, refetch } = useFetchAllDevis(params);
  const { unvalidate } = useUnvalidateDevis();
  const { handleDelete, isLoading: isDeleting } = useDeleteDevis();
  const { transfer } = useTransferDevis();
  const [hoveredRow, setHoveredRow] = useState(null);
  const [unvalidatingId, setUnvalidatingId] = useState(null);

  const handleDevisCreated = (devisId) => {
    setIsDrawerOpen(false);
    onSelectDevis(devisId);
  };

  const handleUnvalidateDevis = async (id) => {
    try {
      setUnvalidatingId(id);
      await unvalidate(id);
      message.success('Devis dévalidé avec succès');
      await refetch();
    } catch (err) {
      message.error(`Échec de la dévalidation du devis: ${err.message || err}`);
    } finally {
      setUnvalidatingId(null);
    }
  };

  const handleDeleteDevis = async (id) => {
    try {
      await handleDelete(id);
      message.success('Devis supprimé avec succès');
      refetch();
    } catch (err) {
      message.error(`Échec de la suppression du devis: ${err.message}`);
    }
  };

  const rowSelection = activeTab === 'validated' ? {
    selectedRowKeys,
    preserveSelectedRowKeys: true,
    onChange: (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: record.TRANSFERER, // Disable selection for already transferred devis
    }),
    // This prevents the entire cell from triggering row selection
    onCell: () => ({
      onClick: e => {
        e.stopPropagation();
      }
    }),

  } : undefined;

  // Handle row click with position capture
  const handleRowClick = (record, event) => {
    if (captureClickEvent && event) {
      onSelectDevis(record.id, event);
    } else {
      onSelectDevis(record.id);
    }
  };

  const dateColumn = {
    title: activeTab === 'notValidated' ? 'Date Dev.' : 'Date Valid.',
    dataIndex: activeTab === 'validated' ? 'DATEVALID' : 'DATE_DEV',
    key: 'date',
    render: (date) => dayjs(date).format('DD/MM/YYYY'),
    sorter: true,
    sortOrder: sorter.field === (activeTab === 'validated' ? 'DATEVALID' : 'DATE_DEV') ? sorter.order : null,
    // Add this to prevent click events from bubbling up to the row
    onCell: () => ({
      onClick: e => {
        e.stopPropagation();
        return false;
      }
    })
  };

  const referenceColumn = {
    title: 'Ref Dev.',
    dataIndex: 'REF_DEV',
    key: 'REF_DEV',
    sorter: true,
    sortOrder: sorter.field === 'REF_DEV' ? sorter.order : null,
    filteredValue: refSearch ? [refSearch] : [],
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder="Commence par"
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => {
            confirm();
            setRefSearch(selectedKeys[0] || '');
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
          style={{ marginBottom: 8, display: 'block' }}
          ref={node => node && setTimeout(() => node.focus(), 0)}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              confirm();
              setRefSearch(selectedKeys[0] || '');
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Rechercher
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              setRefSearch('');
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            size="small"
            style={{ width: 90 }}
          >
            Réinitialiser
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    render: (text, record) => (
      <span 
        style={{ 
          position: 'relative',
          fontWeight: hoveredRow === record.id ? 'bold' : 'normal',
          transition: 'font-weight 0.3s ease'
        }}
      >
        {text}
        <span
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: hoveredRow === record.id ? '100%' : '0%',
            height: '1px',
            backgroundColor: '#000',
            transition: 'width 0.3s ease'
          }}
        />
      </span>
    )
  };

  const priceColumn = {
    title: 'Total TTC',
    dataIndex: 'MNT_TTC',
    key: 'MNT_TTC',
    render: value => value?.toFixed(2),
    sorter: true,
    sortOrder: sorter.field === 'MNT_TTC' ? sorter.order : null,
  };

  const clientColumn = {
    title: 'Client',
    dataIndex: 'CLIENT',
    key: 'CLIENT',
    width: 400,
    filteredValue: clientSearch ? [clientSearch] : [],
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder="Rechercher Client"
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => {
            confirm();
            setClientSearch(selectedKeys[0] || '');
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
          style={{ marginBottom: 8, display: 'block' }}
          ref={node => node && setTimeout(() => node.focus(), 0)}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              confirm();
              setClientSearch(selectedKeys[0] || '');
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Rechercher
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              setClientSearch('');
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            size="small"
            style={{ width: 90 }}
          >
            Réinitialiser
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
  };

  const compteColumn = {
    title: 'Compte',
    dataIndex: 'COMPTE',
    key: 'COMPTE',
    filteredValue: compteSearch ? [compteSearch] : [],
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder="Rechercher Compte"
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => {
            confirm();
            setCompteSearch(selectedKeys[0] || '');
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
          style={{ marginBottom: 8, display: 'block' }}
          ref={node => node && setTimeout(() => node.focus(), 0)}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              confirm();
              setCompteSearch(selectedKeys[0] || '');
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Rechercher
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              setCompteSearch('');
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            size="small"
            style={{ width: 90 }}
          >
            Réinitialiser
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
  };

  const handlePrintClick = (devisId) => {
    setSelectedDevisForPrint(devisId);
    setIsPrintDialogOpen(true);
  };

  const handlePrint = async (printType) => {
    try {
      if (isBulkPrinting && printType === 'external') {
        await openVentileDevis(selectedDevisForBulkPrint);
        message.success('Ventilés A5 préparés par étage pour tous les devis sélectionnés');
        return;
      }
      if (!selectedDevisData) {
        throw new Error('Missing required data for printing');
      }

      await printDevis(selectedDevisData, selectedDevisItems || [], commercialData, printType);
      message.success('Document préparé pour l\'impression');
    } catch (error) {
      console.error('Error printing devis:', error);
      message.error(`Échec de l'impression: ${error.message}`);
    }
  };

  const handlePreview = async (printType) => {
    try {
      if (isBulkPrinting && printType === 'external') {
        await openVentileDevis(selectedDevisForBulkPrint, true);
        return;
      }
      if (!selectedDevisData) {
        throw new Error('Missing required data for preview');
      }
      
      await previewDevis(selectedDevisData, selectedDevisItems || [], commercialData, printType);
    } catch (error) {
      console.error('Error previewing devis:', error);
      message.error(`Échec de l'aperçu: ${error.message}`);
    }
  };

  const handlePrintEmplacement = () => {
    printDevisEmplacement(selectedDevisData, commercialData);
  };
  
  const handlePreviewEmplacement = () => {
    previewDevisEmplacement(selectedDevisData, commercialData);
  };

  const columns = [
    dateColumn,
    referenceColumn,
    compteColumn,
    clientColumn,
    priceColumn,
    {
      title: 'Remarque',
      dataIndex: 'REMARQUE',
      key: 'REMARQUE',
      ellipsis: true,
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_, record) => {
        const canUnvalidate = (userPermissions.isManager || userPermissions.isDevisManager) && record.VALIDER && !record.TRANSFERER;
        const canDelete = currentCan('quotes', 'delete') && (activeTab === 'notValidated');
        return (
          <Space>
            <PermissionButton permissionModule="quotes" permissionAction="print"
              icon={<PrinterOutlined />} 
              onClick={(e) => {
                e.stopPropagation();
                handlePrintClick(record.id);
              }}
            />
            {canDelete && (
              <Popconfirm
                title="Êtes-vous sûr de vouloir supprimer ce devis?"
                onConfirm={(e) => {
                  e.stopPropagation();
                  handleDeleteDevis(record.id);
                }}
                onCancel={(e) => e.stopPropagation()}
                okText="Oui"
                cancelText="Non"
              >
                <PermissionButton permissionModule="quotes" permissionAction="delete"
                  icon={isDeleting ? <LoadingOutlined /> : <DeleteOutlined />} 
                  danger
                  disabled={isDeleting}
                  onClick={(e) => e.stopPropagation()}
                />
              </Popconfirm>
            )}
            {canUnvalidate && (
              <Tooltip title="Dévalider">
                <PermissionButton permissionModule="quotes" permissionAction="unvalidate"
                  type="default"
                  icon={<RollbackOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUnvalidateDevis(record.id);
                  }}
                  loading={unvalidatingId === record.id}
                />
              </Tooltip>
            )}
          </Space>
        );
      },
      onCell: () => ({
        onClick: e => e.stopPropagation(),
      }),
    },
  ];

  const handleBulkTransfer = async () => {
    if (!selectedRowKeys.length) {
      message.warning('Veuillez sélectionner des devis à transférer');
      return;
    }
  
    setIsTransferring(true);
    try {
      for (const id of selectedRowKeys) {
        await transfer(id);
      }
      message.success(`${selectedRowKeys.length} devis transférés avec succès`);
      setSelectedRowKeys([]);
      refetch();
    } catch (error) {
      message.error('Échec du transfert de certains devis: ' + error.message);
    } finally {
      setIsTransferring(false);
    }
  };

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setPagination(prev => ({ ...prev, current: 1 }));
    setSelectedRowKeys([]); // Clear selections when changing tabs
    
    // Update the sorter based on the active tab
    setSorter({
      field: 'REF_DEV',
      order: newTab === 'transferred' ? 'descend' : 'ascend'
    });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setPagination({ current: pagination.current, pageSize: pagination.pageSize });
    setSorter({ field: sorter.field || 'REF_DEV', order: sorter.order || null });
  };

  const getRowStyle = (record) => {
    if (record.TRANSFERER) {
      return { backgroundColor: '#e6f7ff' }; // Light blue for transferred
    }
    if (activeTab === 'validated') {
      return { backgroundColor: '#f6ffed' }; // Light green for validated
    }
    return {};
  };

  const handleDateChange = (dates) => {
    setDateRange(dates || [null, null]);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const clearAllFilters = () => {
    setClientSearch('');
    setCompteSearch('');
    setRefSearch('');
    setDateRange([null, null]);
    setStartDate(null);
    setEndDate(null);
    setSelectedCommercials([]);
    setSelectedRowKeys([]);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleBulkPrint = async () => {
    if (!selectedRowKeys.length) {
      message.warning('Veuillez sélectionner des devis à imprimer');
      return;
    }

    const selectedDevis = selectedRowKeys.map(id => devis.find(d => d.id === id) || { id });
    setSelectedDevisForBulkPrint(selectedDevis);
    setIsBulkPrinting(true);
    setCurrentPrintingIndex(0);
    setShouldBreakPrinting(false);
    
    // Start the printing process by showing the first modal
    const firstDevis = selectedDevis[0];
    setSelectedDevisForPrint(firstDevis.id);
    setIsPrintDialogOpen(true);
  };

  const handleNextDevis = () => {
    if (currentPrintingIndex < selectedDevisForBulkPrint.length - 1) {
      setCurrentPrintingIndex(prev => prev + 1);
      const nextDevis = selectedDevisForBulkPrint[currentPrintingIndex + 1];
      setSelectedDevisForPrint(nextDevis.id);
    }
  };

  const handlePreviousDevis = () => {
    if (currentPrintingIndex > 0) {
      setCurrentPrintingIndex(prev => prev - 1);
      const previousDevis = selectedDevisForBulkPrint[currentPrintingIndex - 1];
      setSelectedDevisForPrint(previousDevis.id);
    }
  };

  const handleClosePrintDialog = () => {
    setIsPrintDialogOpen(false);
    setIsBulkPrinting(false);
    setSelectedDevisForBulkPrint([]);
    setCurrentPrintingIndex(0);
    setShouldBreakPrinting(false);
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    setExportProgress({ stage: 'starting', table: 'Devis', percent: 0 });
    try {
      const options = {};
      if (startDate) options.startDate = startDate;
      if (endDate) options.endDate = endDate;
      
      const result = await downloadService.exportWithProgress({ ...options, type: 'devis', onProgress: setExportProgress });
      setExportProgress(result);
      
      if (result.ftpUpload && result.ftpUpload.success) {
        message.success('Export des devis complété avec succès');
      } else {
        message.error('Échec de l\'export: ' + (result.message || 'Une erreur est survenue'));
      }
    } catch (error) {
      setExportProgress(previous => ({ ...previous, stage: 'failed', message: error.message }));
      console.error('Erreur d\'export:', error);
      message.error('Échec de l\'export: ' + (error.message || 'Une erreur inconnue est survenue'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleRunDevisDropImport = async () => {
    try {
      setIsCheckingImport(true);
      // Get selected files from localStorage
      const savedSelection = localStorage.getItem('devis_drop_selected_files');
      const selectedFiles = savedSelection ? JSON.parse(savedSelection) : [];
      
      // If no files are selected, import all files (default behavior)
      if (selectedFiles.length === 0) {
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/import/devis-drop/run`);
        message.success('Import de tous les devis modifiés exécuté avec succès.');
      } else {
        // Import only selected files
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/import/devis-drop/import-selected`, {
          selectedFiles
        });
        message.success(`Import de ${selectedFiles.length} fichier(s) sélectionné(s) avec succès.`);
      }
      
      await refetch();
    } catch (error) {
      message.error(`Échec de l'import: ${error?.response?.data?.message || error.message}`);
    } finally {
      setIsCheckingImport(false);
    }
  };

  const handleFileImportComplete = (processedFiles) => {
    message.success(`${processedFiles.length} fichier(s) importé(s) avec succès`);
    refetch();
  };

  const handleRefetchDevis = async () => {
    try {
      await refetch();
      message.success('Liste des devis actualisée');
    } catch (error) {
      message.error(`Échec de l'actualisation: ${error?.message || 'Erreur inconnue'}`);
    }
  };

  return (
    <div className="devis-list-container">
      <ExportProgress progress={exportProgress} title="Devis" />
      <Card 
        style={{ 
          marginTop: 20, 
          backgroundColor: '#fff', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
        className="devis-card"
      >
        <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#333', margin: 0 }}>Liste Des Devis</h2>
            <Tooltip title="Actualiser la liste">
              <Button 
                onClick={handleRefetchDevis}
                icon={<ReloadOutlined />}
                type="text"
              />
            </Tooltip>
          </div>
          <Space>
            {activeTab === 'validated' && selectedRowKeys.length > 0 && (
              <PermissionButton permissionModule="quotes" permissionAction="transfer"
                type="primary"
                icon={<SwapOutlined />}
                onClick={handleBulkTransfer}
                loading={isTransferring}
              >
                Transférer Sélectionnés ({selectedRowKeys.length})
              </PermissionButton>
            )}
            
            {activeTab === 'validated' && selectedRowKeys.length > 0 && (
              <PermissionButton permissionModule="quotes" permissionAction="print"
                type="primary"
                icon={<PrinterOutlined />}
                onClick={handleBulkPrint}
                disabled={isBulkPrinting}
              >
                Imprimer Sélectionnés ({selectedRowKeys.length})
              </PermissionButton>
            )}
            {activeTab !== 'notValidated' && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title={showDateFilters ? "Masquer les filtres de date" : "Afficher les filtres de date"}>
                  <Button 
                    type="text" 
                    icon={<MoreOutlined />} 
                    onClick={() => setShowDateFilters(!showDateFilters)}
                    style={{ marginRight: '8px' }}
                  />
                </Tooltip>
                <div 
                  style={{ 
                    display: 'flex', 
                    maxWidth: showDateFilters ? '300px' : '0',
                    overflow: 'hidden',
                    transition: 'max-width 0.3s ease-in-out',
                    marginRight: showDateFilters ? '8px' : '0'
                  }}
                >
                  <DatePicker
                    placeholder="Date début"
                    onChange={(date) => setStartDate(date)}
                    value={startDate}
                    style={{ 
                      width: '130px',
                      marginRight: '4px'
                    }}
                  />
                  <DatePicker
                    placeholder="Date fin"
                    onChange={(date) => setEndDate(date)}
                    value={endDate}
                    style={{ width: '130px' }}
                  />
                </div>
                
              </div>
            )}
            {activeTab === 'notValidated' && null}
            {activeTab !== 'notValidated' && (
              <Button
                  type="primary"
                  icon={<CloudUploadOutlined />}
                  loading={isExporting}
                  onClick={handleExport}
                >
                  Exporter
                </Button>
            )}
            {activeTab === 'notValidated'  && (
              <>
                <Tooltip title="Importer devis modifiés depuis Celer (tous les fichiers ou seulement ceux sélectionnés)">
                  <Button 
                    onClick={handleRunDevisDropImport}
                    loading={isCheckingImport}
                    icon={<DownloadOutlined />}
                  />
                </Tooltip>
                <Tooltip title="Configurer les fichiers spécifiques à importer (optionnel)">
                  <Button 
                    icon={<SettingOutlined />}
                    onClick={() => setIsFileSelectorVisible(true)}
                  />
                </Tooltip>
              </>
            )}
            <PermissionButton permissionModule="quotes" permissionAction="create"
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={() => setIsDrawerOpen(true)}
            >
              Créer Devis
            </PermissionButton>
          </Space>
        </Space>

        <Form
          layout="inline"
          style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}
        >
          <Form.Item 
            label="Période" 
            style={{ display: 'inline-flex', alignItems: 'center', marginRight: 6, marginBottom: 4 }}
            labelCol={{ style: { display: 'flex', alignItems: 'center', paddingBottom: 0 } }}
          >
            <RangePicker 
              onChange={handleDateChange}
              value={dateRange}
              format="DD/MM/YYYY"
              placeholder={['Date début', 'Date fin']}
              allowClear
              size="small"
              style={{ width: 260 }}
            />
          </Form.Item>
          <Form.Item style={{ marginRight: 6, marginBottom: 4 }}>
            <Tooltip title="Réinitialiser période">
              <Button 
                icon={<FilterOutlined />} 
                onClick={() => {
                  setDateRange([null, null]);
                  setPagination(prev => ({ ...prev, current: 1 }));
                }}
                size="small"
              >
                
              </Button>
            </Tooltip>
          </Form.Item>
          <Form.Item 
            label="Commercials" 
            style={{ display: 'inline-flex', alignItems: 'center', marginRight: 6, marginBottom: 4 }}
            labelCol={{ style: { display: 'flex', alignItems: 'center', paddingBottom: 0 } }}
          >
            <Select
              mode="multiple"
              allowClear
              labelInValue
              placeholder="Sélectionner commerciaux"
              value={selectedCommercials}
              onChange={(vals) => {
                // vals will be array of { value, label }
                setSelectedCommercials(Array.isArray(vals) ? vals : []);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              loading={comLoading}
              options={(allCommercials || []).map(c => ({ label: `${c.nom} (${c.code_com})`, value: c.code_com }))}
              style={{ width: 360 }}
              size="small"
              tagRender={({ label, onClose }) => {
                const onMouseDown = (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                };
                // Always display first 4 chars of name and the ID in parentheses when available
                let displayLabel = label;
                if (typeof label === 'string') {
                  const m = label.match(/^(.*?)\s*\((.*?)\)\s*$/);
                  if (m) {
                    const name = (m[1] || '').trim();
                    const id = (m[2] || '').trim();
                    displayLabel = `${name.slice(0, 4)} (${id})`;
                  } else {
                    displayLabel = label.slice(0, 4);
                  }
                }
                return (
                  <span
                    onMouseDown={onMouseDown}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      margin: 2,
                      padding: '0 6px',
                      borderRadius: 10,
                      background: '#f2f3f5',
                      fontSize: 12,
                      height: 20,
                      lineHeight: '20px'
                    }}
                  >
                    <span style={{ marginRight: 4 }}>{displayLabel}</span>
                    <CloseOutlined onClick={onClose} style={{ fontSize: 10 }} />
                  </span>
                );
              }}
            />
          </Form.Item>
          <Form.Item style={{ marginRight: 6, marginBottom: 4 }}>
            <Tooltip title="Réinitialiser commerciaux">
              <Button 
                icon={<FilterOutlined />} 
                onClick={() => {
                  setSelectedCommercials([]);
                  setPagination(prev => ({ ...prev, current: 1 }));
                }}
                size="small"
              >
                
              </Button>
            </Tooltip>
          </Form.Item>
          <Form.Item style={{ marginRight: 6, marginBottom: 4 }}>
            <Tooltip title="Effacer tous les filtres">
              <Button
                icon={<ClearOutlined />}
                onClick={clearAllFilters}
                aria-label="Effacer tous les filtres"
                size="small"
              />
            </Tooltip>
          </Form.Item>
        </Form>

        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <TabPane tab="Enregistrés" key="notValidated" />
          <TabPane tab="Validés" key="validated" />
          <TabPane tab="Transférés" key="transferred" />
        </Tabs>

        <Table
          columns={columns}
          dataSource={devis}
          rowKey="id"
          loading={loading}
          pagination={{ 
            current: pagination.current, 
            pageSize: pagination.pageSize, 
            total 
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: (event) => handleRowClick(record, event),
            onMouseEnter: () => setHoveredRow(record.id),
            onMouseLeave: () => setHoveredRow(null),
            style: getRowStyle(record),
            className: 'devis-table-row',
          })}
          rowSelection={rowSelection}
          className="devis-table"
          size="small"
        />

        <Drawer
          title="Créer Nouveau Devis"
          width={920}
          onClose={() => setIsDrawerOpen(false)}
          open={isDrawerOpen}
          bodyStyle={{ paddingBottom: 80 }}
        >
          <DevisForm onDevisCreated={handleDevisCreated} />
        </Drawer>

        <PrintDialog 
          open={isPrintDialogOpen}
          onClose={handleClosePrintDialog}
          onPrint={handlePrint}
          onPreview={handlePreview}
          onPrintEmplacement={handlePrintEmplacement}
          onPreviewEmplacement={handlePreviewEmplacement}
          devisRef={selectedDevisData?.REF_DEV}
          isBulkPrint={isBulkPrinting}
          currentDevisIndex={currentPrintingIndex}
          totalDevis={selectedDevisForBulkPrint.length}
          onNextDevis={handleNextDevis}
          onPreviousDevis={handlePreviousDevis}
          hasNextDevis={currentPrintingIndex < selectedDevisForBulkPrint.length - 1}
          hasPreviousDevis={currentPrintingIndex > 0}
        />

        <DevisDropFileSelector
          visible={isFileSelectorVisible}
          onClose={() => setIsFileSelectorVisible(false)}
          onImport={handleFileImportComplete}
        />
      </Card>
    </div>
  );
};

export default DevisList;
